import { memo, useRef, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import { ShaderMaterial, PlaneGeometry } from 'three'
import { TARGET_FRAMERATE } from '../utils'

const fadeGeometry = new PlaneGeometry(2, 2)
const fadeMaterial = new ShaderMaterial({
  transparent: true,
  depthTest: false,
  depthWrite: false,
  uniforms: {
    uOpacity: { value: 1 },
  },
  vertexShader: `
    void main() {
      gl_Position = vec4(position.xy, 0.0, 1.0);
    }
  `,
  fragmentShader: `
    uniform float uOpacity;
    void main() {
      gl_FragColor = vec4(0.0, 0.0, 0.0, uOpacity);
    }
  `,
})

interface CameraFadeProps {
  activeCam: number
}

function CameraFade({ activeCam }: CameraFadeProps) {
  const phase = useRef<'idle' | 'out' | 'long'>('long')
  const opacity = useRef(1)
  const prevCam = useRef(activeCam)

  useEffect(() => {
    if (prevCam.current !== activeCam) {
      opacity.current = 1
      phase.current = 'out'
      prevCam.current = activeCam
    }
  }, [activeCam])

  const FADE_SPEED = 0.04 * TARGET_FRAMERATE

  useFrame((_, delta) => {
    if (phase.current === 'long') {
      opacity.current -= (FADE_SPEED / 5) * delta
      if (opacity.current <= 0) {
        opacity.current = 0
        phase.current = 'idle'
      }
    } else if (phase.current === 'out') {
      opacity.current -= FADE_SPEED * delta
      if (opacity.current <= 0) {
        opacity.current = 0
        phase.current = 'idle'
      }
    }
    fadeMaterial.uniforms.uOpacity.value = opacity.current
  })

  return (
    <mesh geometry={fadeGeometry} material={fadeMaterial} frustumCulled={false} renderOrder={9999} />
  )
}

export default memo(CameraFade)
